import { PaginatedResult, PaginationRequest, SortedRequest, SystemShare, SystemShareType } from "@ews/react-data";
import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonCol, IonContent, IonPage, IonRow } from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Locale } from "../../../../setup/locale";
import FilterList, { isFilterActive } from "../../../FilterList/FilterList";
import { FilterState } from "../../../FilterList/types";
import List from "../../../List/List";
import { defaultPageRange } from "../../../List/Pagination/pagerange";
import { HeaderOption } from "../../../List/types";
import { useNamedRoutes } from "../../../NamedRoutes";
import { usePersistentState } from "../../../Persistence";
import { useFetchSystemShares, useSystem } from "../../../ReactData/system";
import { useIsMobile } from "../../../Responsive";
import SettingsTextInput from "../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { Filter } from "../types/reportTypes";
import NewShare from "./Component/NewShare/NewShare";
import ValidityCountDown from "./Component/ValidityCountDown";
import style from "./Shares.module.scss";
import { ReleasesTypes } from "./types";

type PersistenceFilter = SortedRequest & FilterState;

const standardFilter: Filter<{}> = {
    pageSize: defaultPageRange
};
const defaultPaginationResult =
{
    currentPage: 0,
    pageSize: standardFilter.pageSize || defaultPageRange,
    totalCountOfEntries: 0,
    totalCountOfPages: 0,
    results: [],
};

const link = { url: "system:share-settings", paramsKey: "systemId,userId" };

const ignoreKeys: (keyof PersistenceFilter)[] = ["pageSize", "isFilterOpen", "page", "pageSize", "sortCriteria", "sortOrder"];


const Shares: React.FC<ReleasesTypes> = ({
    filter,
    onIsOpen,
    onFilterActive
}) =>
{

    const filterDebounceTime = 900;
    const { params } = useNamedRoutes();
    const { translate: t } = useL10n();
    const { id: systemId } = params();
    const { timezone } = useSystem(systemId || "");

    const mobileView = useIsMobile();

    const header: HeaderOption<Locale, SystemShare>[] = useMemo(() =>
    {
        return [
            {
                title: 'COM_FACPMANAGEMENT_USERS_CUSTOMERID',
                className: style.customerNumber,
                key: 'customerNumber',
                breakpoint: 1032,
                up: true
            },
            {
                title: 'COM_FACPMANAGEMENT_BALANCE_CUSTOMER_LABEL',
                className: style.customerName,
                key: 'customerName',
                breakpoint: 1232,
                up: true
            },
            {
                title: 'COM_FACPMANAGEMENT_USERS_USERID',
                className: style.username,
                key: 'username',
                both: true
            },
            {
                title: 'COM_FACPMANAGEMENT_USERS_FIRSTNAME',
                className: style.userFirstName,
                key: 'userFirstName',
                breakpoint: 922,
                up: true
            },
            {
                title: 'COM_FACPMANAGEMENT_USERS_LASTNAME',
                className: style.userLastName,
                key: 'userLastName',
                both: true
            },
            {
                title: '[REACT_SHARE_TYPE]',
                className: style.type,
                key: 'type',
                child: (item) => t(`[REACT_SHARE_TYPE_${item.type}]`),
                both: true
            },
            {
                title: '[REACT_VALID_FOR]',
                className: style.validFor,
                key: 'validFor',
                child: (item) => (item.type === SystemShareType.EXTENDED && item.validFor && item.validFor > 0) ?
                    <ValidityCountDown validFrom={item.validFromUTC!} validTo={item.validToUTC!} /> :
                    <>{t('COM_BANNERS_FIELD_VALUE_1')}</>,
                both: true
            }
            // { title: 'COM_FACPMANAGEMENT_USER_FIELD_NAME_LABEL', key: 'username', breakpoint: 1200, up: true },
            // { title: 'Remaining Time', key: 'remainingTime', breakpoint: 1200, up: true },
        ];
    }, [t]);

    //const [search, setSearch] = useState(0);
    //const [currentRowLimit, setCurrentRowLimit] = useState<number>(pageRange[1]);


    const [result, setResult] = useState<PaginatedResult<SystemShare>>(defaultPaginationResult);
    //const [userId, setUserId] = useState<string>("");

    //const [filterDetailsOpen, setFilterDetailsOpen] = useState(true);

    const fetchSystemShares = useFetchSystemShares();

    const [currentState, setState] = usePersistentState<PersistenceFilter>('systems-share:filter', standardFilter as PersistenceFilter);

    const sortCriteria = useRef<{
        key: string;
        direction: "ASC" | "DESC";
    } | undefined>(undefined);


    const onPageChange = useCallback((page: PersistenceFilter["page"]) => setState((prev) => ({ ...prev, page })), [setState]);
    const onSortChange = useCallback((sortCriteria: PersistenceFilter["sortCriteria"], sortOrder: PersistenceFilter["sortOrder"]) => setState((prev) => ({ ...prev, sortCriteria, sortOrder })), [setState]);
    const onPageSizeChange = useCallback((pageSize: PersistenceFilter["pageSize"]) => setState((prev) => ({ ...prev, pageSize })), [setState]);
    const onReloadChange = useCallback(() => setState((prev) => ({ ...prev })), [setState]);


    const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
    const onResetFilter = useCallback(() => setState(() => standardFilter), [setState]);

    const isFilterChanged = isFilterActive<PersistenceFilter>(currentState, standardFilter, ignoreKeys);

    useEffect(() =>
    {
        if (filter?.isFilterActive !== isFilterChanged) {
            onFilterActive && onFilterActive(isFilterChanged);
        }
    }, [isFilterChanged, filter, onFilterActive]);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    // const [list, setList] = useState<any[] | null>(data);

    // const [filterDetailsOpen, setFilterDetailsOpen] = useState(false);

    // const eventSelectValue = useRef('');

    // type MaintenanceReportFilter = {
    //     systemId?: StingFilterType;
    //     customerId?: StingFilterType;
    //     customername?: StingFilterType;
    //     userId?: StingFilterType;
    //     username?: StingFilterType;
    //     remainingTime?: StingFilterType;
    // };

    // const [currentState, setState] = useState<MaintenanceReportFilter>({});

    // const [clearFilterInputs, setClearFilterInputs] = useState(false);

    const onOpenModal = useCallback(() => { setIsOpen(prev => true); }, [setIsOpen]);

    const handleFilterResponse = useCallback(async (filterCriteria: PaginationRequest) =>
    {
        let response: PaginatedResult<SystemShare> | undefined = undefined;
        try {
            response = await fetchSystemShares(systemId!, filterCriteria);
        }
        catch (e) {
            console.error(`Shares.tsx: handleFilterResponse: error during fetchSystemShares: ${e}`);
            // return;
        }

        if (!response) {
            response = defaultPaginationResult;
        }


        setResult(response);
    }, [fetchSystemShares, systemId]);

    useEffect(() =>
    {
        handleFilterResponse(currentState);
    }, [currentState, handleFilterResponse]);

    useEffect(() =>
    {
        sortCriteria.current =
            currentState.sortCriteria ? {
                key: currentState.sortCriteria,
                direction: currentState.sortOrder || "ASC"
            } : undefined;

    }, [currentState.sortCriteria, currentState.sortOrder]);

    return <IonPage>
        <IonContent>

            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <FilterList
                    isOpen={filter?.isFilterOpen || false}
                    isDetailsOpen={filter?.isDetailOpen || false}
                    onIsOpen={onIsFilterOpen}
                    onReset={onResetFilter}
                    noPrint
                >
                    <IonCol>
                        <SettingsTextInput
                            id="username"
                            text="COM_FACPMANAGEMENT_USERS_USERID"
                            debounce={filterDebounceTime}
                            value={currentState["username"]}
                            onChange={(value) =>
                            {
                                const currentFilter = { ...currentState };

                                if (value.length) currentFilter["username"] = value;
                                else delete currentFilter["username"];

                                setState(currentFilter);
                            }}
                        />
                    </IonCol>
                    <IonCol>
                        <IonRow class="ion-justify-content-end">
                            <IonButton
                                color={"success"}
                                onClick={onOpenModal}
                            >
                                {t('COM_FACPMANAGEMENT_SHARE_ADD_NEW')}
                            </IonButton>
                        </IonRow>
                    </IonCol>
                </FilterList>
                <List
                    rowClass={style.row}
                    rows={result.results}
                    sort={sortCriteria.current}
                    pageSize={result.pageSize}
                    onSort={onSortChange}
                    onPageChange={onPageChange}
                    onPageSize={onPageSizeChange}
                    onReload={onReloadChange}
                    currentPage={result.currentPage}
                    numberOfPages={result.totalCountOfPages}
                    link={link}

                    maxHeight={mobileView ? '86vh' : "59vh"}

                    headers={header}
                />
            </div>
            <NewShare
                isOpen={isOpen}
                systemId={systemId}
                onClose={() =>
                {
                    setIsOpen(false);
                }} />
        </IonContent>
    </IonPage>;
};

export default Shares;
